import { useQuery } from '@apollo/client';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import { R300 } from '@atlaskit/theme/colors';
import {
  BillingStatus,
  FetchLinkedEntitlementsForTableQuery,
  FetchLinkedEntitlementsForTableQueryVariables,
  LinkedDestinationEntitlement,
  LinkedEntitlementTypes,
  LinkedProductTypes,
  Maybe,
} from '@css-xp/graphtypes/src/generated-types-new';
import { NEW_DESIGN_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import { NOT_AVAILABLE, EntitlementEdition } from '@atlassian/cssxp-utils-entitlement';
import React, { useEffect, useMemo } from 'react';
import { SandboxLozenge } from '../../../copyable-text';
import { CustomPanel } from '../../../custom-panel';
import { DynamicResultTable } from '../../../dynamic-result-table';
import EntitlementIcon from '../../../entitlement-icon/entitlement-icon';
import { FormattedDate } from '../../../formattedV2';
import { withApollo } from '../../../graphql-provider';
import { fetchLinkedEntitlementsForTable } from '../queries/fetch-linked-entitlements';
import { getProductName } from '../utils';
import './linked-entitlements-table.scss';

interface LinkedEntitlementsTableProps {
  entitlementId?: string;
  message?: string;
  header?: string;
  showBillDateColumn?: boolean;
  currentEntitlementType?: LinkedEntitlementTypes;
  destinationEntitlementType?: Array<LinkedEntitlementTypes>;
  productType?: Maybe<LinkedProductTypes>;
  parentContainerID?: string;
  checkForLastParentExistence?: boolean;
  findProducts?: boolean;
  findApps?: boolean;
  findAllEntitlements?: boolean;
  updateLinkedEntitlements?: (data: any) => void;
  setLoading?: (data: boolean) => void;
  linkedEntsCount?: (data: any) => void;
  linkedEntitlementsData?: any;
  linkedEntitlementsLoading?: boolean;
  linkedEntitlementsError?: Error;
}

const emptyStateOrderProp = {
  header: '',
  description: 'No other subscriptions will be affected',
  imageUrl: '',
  testId: 'no-results-subscriptions-apps',
};

export const LinkedEntitlementsTable = ({
  entitlementId = '',
  message,
  header,
  parentContainerID,
  showBillDateColumn = false,
  currentEntitlementType,
  destinationEntitlementType,
  productType,
  findApps = false,
  findProducts = false,
  checkForLastParentExistence = false,
  updateLinkedEntitlements,
  setLoading,
  linkedEntsCount,
  linkedEntitlementsData,
  linkedEntitlementsLoading,
  linkedEntitlementsError,
}: LinkedEntitlementsTableProps) => {
  let { data, loading, error } = useQuery<
    FetchLinkedEntitlementsForTableQuery,
    FetchLinkedEntitlementsForTableQueryVariables
  >(fetchLinkedEntitlementsForTable, {
    variables: {
      id: entitlementId,
      searchLinkedEntitlementParams: {
        entitlementId: entitlementId,
        containerId: parentContainerID ?? undefined,
        currentEntitlementType,
        destinationEntitlementType,
        productType,
        findApps,
        findProducts,
        checkForLastParentExistence,
      },
    },
    displayName: 'fetchAllLinkedEntitlements',
    notifyOnNetworkStatusChange: true,
    skip: linkedEntitlementsData || linkedEntitlementsLoading || linkedEntitlementsError,
  });

  if (linkedEntitlementsData) {
    data = linkedEntitlementsData;
  }

  const linkedEntitlements: Array<LinkedDestinationEntitlement> = useMemo(() => {
    return (
      (data?.entitlementDetails.linkedEntitlements?.filter(
        (linkedEntitlementObj) => linkedEntitlementObj?.billingStatus === BillingStatus.ACTIVE
      ) as Array<LinkedDestinationEntitlement>) || []
    );
  }, [data]);

  const visibleLinkedEnts = useMemo(() => {
    let filterLinkedEntsWithoutContainerId;
    if (productType === LinkedProductTypes.JIRA && parentContainerID) {
      filterLinkedEntsWithoutContainerId = linkedEntitlements.filter(
        (linkedEnt) =>
          !(
            (linkedEnt.isSandbox && linkedEnt.edition?.toUpperCase() === EntitlementEdition.FAMILY_SANDBOX) ||
            linkedEnt.entitlementId === parentContainerID
          )
      );
    } else {
      filterLinkedEntsWithoutContainerId = linkedEntitlements.filter(
        (linkedEnt) => !(linkedEnt.isSandbox && linkedEnt.edition?.toUpperCase() === EntitlementEdition.FAMILY_SANDBOX)
      );
    }
    return filterLinkedEntsWithoutContainerId;
  }, [linkedEntitlements]);

  useEffect(() => {
    setLoading && setLoading(loading);
  }, [loading, setLoading]);

  useEffect(() => {
    linkedEntsCount && visibleLinkedEnts.length > 0 && linkedEntsCount(1 + visibleLinkedEnts.length);
  }, [visibleLinkedEnts, linkedEntsCount]);

  useEffect(() => {
    updateLinkedEntitlements && linkedEntitlements.length > 0 && updateLinkedEntitlements(linkedEntitlements);
  }, [linkedEntitlements, updateLinkedEntitlements]);

  const getRows = () => {
    return visibleLinkedEnts.map((item: LinkedDestinationEntitlement, index) => {
      const cellData = [
        {
          content: (
            <>
              <EntitlementIcon
                logoId={item?.logoId as string}
                productId={item?.productId as string}
                isCloud={true}
                className="avatar medium"
              />{' '}
              <span className="product-name">{getProductName(item)}</span>
              {item?.isSandbox && <SandboxLozenge />}
            </>
          ),
        },
        {
          content: item?.entitlementNumber ? (
            <span className="entitlement-number">{item?.entitlementNumber}</span>
          ) : (
            <span className="entitlement-number" aria-label="Not available" data-testid="not-available">
              {NOT_AVAILABLE}
            </span>
          ),
        },
      ];

      if (showBillDateColumn) {
        cellData.splice(1, 0, {
          content: item?.latestPeriodEndDate ? (
            <FormattedDate date={item?.latestPeriodEndDate as string} dateFormat={NEW_DESIGN_DATE_FORMAT} />
          ) : (
            <span aria-label="Not available" data-testid="not-available">
              {NOT_AVAILABLE}
            </span>
          ),
        });
      }

      return {
        key: item.entitlementId ? item.entitlementId : index.toString(),
        cells: cellData,
      };
    });
  };

  const getHead = () => {
    const cells = [
      {
        content: 'Product',
        shouldTruncate: true,
        width: 30,
      },
      {
        content: 'Entitlement number',
        width: 20,
      },
    ];

    if (showBillDateColumn) {
      cells.splice(1, 0, {
        content: 'Bill date',
        width: 20,
      });
    }

    return {
      cells,
    };
  };

  return (
    <div className="linked-entitlements-table-container">
      <div>
        {' '}
        <p className="n-300">
          {message ? message : 'All the following subscriptions and the apps will also be affected with this action'}
        </p>
      </div>
      <CustomPanel
        testId="linked-ents"
        expandedHeader={header ? header : `View subscriptions`}
        collapsedHeader={header ? header : `View subscriptions`}
      >
        {error || linkedEntitlementsError ? (
          <div className="error-block">
            <ErrorIcon label="Error" size="medium" primaryColor={R300} />
            <span className="error-text">
              It looks like something went wrong. <span className="n-300">Please go back and try again.</span>
            </span>
          </div>
        ) : (
          <DynamicResultTable
            boxShadow={false}
            testId="subscriptions-and-apps-table"
            head={getHead()}
            rows={getRows()}
            emptyStateProps={emptyStateOrderProp}
            isLoading={loading || linkedEntitlementsLoading}
          />
        )}
      </CustomPanel>
    </div>
  );
};

export default withApollo(LinkedEntitlementsTable);
